/**
 * El ENCUADRE de la foto del mapa facial (28/9/2026, dirección: «que en el
 * mapa facial se pueda ajustar el zoom de la foto que el paciente ha subido,
 * y así la dejo ajustada como yo quiero y se queda guardada con ese
 * encuadre»).
 *
 * Un encuadre es `{zoom, x, y}`:
 *  · `zoom`: 1 es la foto entera; hasta ZOOM_MAXIMO.
 *  · `x`, `y`: el punto de la foto, en % de la foto, que queda en el CENTRO
 *    del recuadro.
 * Se guarda uno POR FOTO de fondo: `facial_map.encuadres[<id de la foto>]`
 * (se puede cambiar entre «antes», otra «antes» y la «comparativa», y cada
 * una tiene su cara en otro sitio). La cara genérica no lleva encuadre.
 *
 * Lo que se acerca y se mueve es una CAPA con la foto Y los puntos, y el
 * recuadro recorta: los puntos siguen en % de la foto entera, igual que
 * antes, y caen en el mismo sitio de la cara con cualquier encuadre. Un sitio
 * que no sepa del encuadre los pinta bien sobre la foto entera.
 *
 * Aquí están todas las cuentas, sin React. El servidor valida lo mismo
 * (backend/models.py, EncuadreMapaFacial).
 */
export const ZOOM_MAXIMO = 4;
// Una ficha tiene unas pocas fotos; el servidor no admite más de estos.
export const MAX_ENCUADRES = 100;
export const SIN_ENCUADRE = Object.freeze({ zoom: 1, x: 50, y: 50 });

// El id de una foto (un uuid). Es la clave en Mongo: ni «.» ni «$».
const ID_FOTO = /^[A-Za-z0-9_-]{1,64}$/;
// Por debajo de esto es la foto entera (el redondeo al guardar la deja en 1).
const CASI_ENTERA = 1.005;

const numero = (v, porDefecto) => (typeof v === 'number' && Number.isFinite(v) ? v : porDefecto);
const entre = (v, min, max) => Math.min(max, Math.max(min, v));
const redondo = (v) => Math.round(v * 100) / 100;

/**
 * Un encuadre que siempre se puede pintar: zoom entre 1 y ZOOM_MAXIMO y sin
 * que asome el fondo por ningún lado. Lo que no se entienda (un texto, un
 * NaN, `null`, una lista), la foto entera: un encuadre raro nunca rompe la
 * pantalla ni esconde la cara.
 */
export function encuadreSeguro(e) {
  if (!e || typeof e !== 'object' || Array.isArray(e)) return SIN_ENCUADRE;
  const zoom = entre(numero(e.zoom, 1), 1, ZOOM_MAXIMO);
  if (zoom < CASI_ENTERA) return SIN_ENCUADRE;
  // Con zoom z se ve 100/z % de la foto: el centro no puede quedar más cerca
  // del borde que la mitad de eso.
  const medio = 50 / zoom;
  return {
    zoom,
    x: entre(numero(e.x, 50), medio, 100 - medio),
    y: entre(numero(e.y, 50), medio, 100 - medio),
  };
}

export const esLaFotoEntera = (e) => encuadreSeguro(e).zoom === 1;

export const mismoEncuadre = (a, b) => {
  const [p, q] = [encuadreSeguro(a), encuadreSeguro(b)];
  return p.zoom === q.zoom && p.x === q.x && p.y === q.y;
};

/**
 * El `transform` de la capa (foto + puntos), con `transform-origin: 0 0`.
 * Un punto de la foto en (px, py) % queda en el recuadro en
 * (50 + zoom·(px − x), 50 + zoom·(py − y)) %: el centro del encuadre, en el
 * centro. El `translate` en % es del tamaño de la capa, que es el del
 * recuadro.
 */
export function transformDe(e) {
  const { zoom, x, y } = encuadreSeguro(e);
  if (zoom === 1) return 'none';
  const r = (v) => Math.round(v * 1000) / 1000;
  return `translate(${r(50 - zoom * x)}%, ${r(50 - zoom * y)}%) scale(${r(zoom)})`;
}

/**
 * Dónde cae EN LA FOTO (en %) un punto del recuadro (en %): la inversa del
 * `transform`. Es lo que se guarda al marcar con la foto acercada.
 */
export function puntoDeLaFoto(e, bx, by) {
  const { zoom, x, y } = encuadreSeguro(e);
  return {
    x: entre(x + (bx - 50) / zoom, 0, 100),
    y: entre(y + (by - 50) / zoom, 0, 100),
  };
}

/**
 * El encuadre con otro `zoom`, dejando el punto de la foto que estaba bajo
 * `antes` (en % del recuadro) bajo `despues`. Sirve para todo:
 *  · botones y deslizador: alrededor del centro (sin `antes`);
 *  · la rueda: alrededor del puntero (`antes` = `despues`);
 *  · arrastrar: el mismo zoom, de `antes` (donde se bajó el dedo) a `despues`;
 *  · pellizcar: el zoom por lo que se han separado los dedos, y el punto
 *    medio de los dedos sigue bajo los dedos.
 */
export function encuadreHacia(e, zoom, antes = { x: 50, y: 50 }, despues = antes) {
  const actual = encuadreSeguro(e);
  const z = entre(numero(zoom, actual.zoom), 1, ZOOM_MAXIMO);
  const px = actual.x + (numero(antes?.x, 50) - 50) / actual.zoom;
  const py = actual.y + (numero(antes?.y, 50) - 50) / actual.zoom;
  return encuadreSeguro({
    zoom: z,
    x: px - (numero(despues?.x, 50) - 50) / z,
    y: py - (numero(despues?.y, 50) - 50) / z,
  });
}

/**
 * Los encuadres de la ficha, como los usa la pantalla: solo los de una foto
 * (clave con forma de id) y que no son la foto entera, ya acotados. Lo que
 * venga raro de la base se queda fuera sin romper nada.
 */
export function encuadresGuardados(todos) {
  const salida = {};
  if (!todos || typeof todos !== 'object' || Array.isArray(todos)) return salida;
  Object.entries(todos).slice(0, MAX_ENCUADRES).forEach(([id, e]) => {
    if (!ID_FOTO.test(id)) return;
    const limpio = encuadreSeguro(e);
    if (limpio.zoom > 1) salida[id] = limpio;
  });
  return salida;
}

/** Los encuadres con el de la foto `id` cambiado (la foto entera, fuera). */
export function conEncuadre(todos, id, e) {
  const salida = { ...(todos || {}) };
  if (!id || !ID_FOTO.test(id)) return salida;
  const limpio = encuadreSeguro(e);
  if (limpio.zoom === 1) delete salida[id];
  else salida[id] = limpio;
  return salida;
}

/** Lo que se manda al servidor: los mismos, con dos decimales. */
export function encuadresParaGuardar(todos) {
  const salida = {};
  Object.entries(encuadresGuardados(todos)).forEach(([id, e]) => {
    const zoom = redondo(e.zoom);
    if (zoom <= 1) return;
    salida[id] = { zoom, x: redondo(e.x), y: redondo(e.y) };
  });
  return salida;
}
